// KB URL shapes (docs/KB.md D3). Categories live at `/{c1}/`, `/{c1}/{c2}/`, … (depth ≤ 3);
// an article at its category's path + `/{slug}/`. A category's stored `path` is the
// slash-joined slug chain WITHOUT leading/trailing slashes ('billing/refunds'), and an
// article's stored `path` is the same for the full URL ('billing/refunds/how-to-refund').
import { KB_LIMITS, PORTAL_PATHS } from '../constants.js';

// The SPA's KB manage view (PORTAL_PATHS.kbManage): /kb, /kb/, /kb/categories, … — a
// tenant route of the React app, never a KB page (`kb` is a reserved root slug).
export const isKbManagePath = (pathname) => /^\/kb(?:\/|$)/.test(String(pathname || ''));

// The manage view lived at /tickets/kb/… until 0.18.13. The new path for an old one
// (/tickets/kb → /kb/, /tickets/kb/tags → /kb/tags), else null. Built from the PATH alone —
// always PORTAL_PATHS.kbManage + the old path's tail, so the result can only be a same-origin
// /kb/… path, never another host (no open redirect). The caller appends the query. Used by
// the server's 301 (app.js) and the SPA's client-side redirect (main.jsx: an installed PWA's
// service worker answers an old /tickets/kb/ URL with the shell without asking the server).
export function legacyKbManagePath(pathname) {
  const m = /^\/tickets\/kb(\/.*)?$/.exec(String(pathname || ''));
  return m ? PORTAL_PATHS.kbManage + (m[1] || '/').slice(1) : null;
}

export const joinKbPath = (parentPath, slug) => (parentPath ? `${parentPath}/${slug}` : slug);

// '' → '/', 'a/b' → '/a/b/'
export const kbHref = (path) => (path ? `/${path}/` : '/');
export const kbCategoryHref = (categoryPath) => kbHref(categoryPath);
export const kbArticleUrl = (categoryPath, slug) => kbHref(joinKbPath(categoryPath, slug));
export const kbEditHref = (path) => `${kbHref(path)}edit/`;
export const kbNewHref = (categoryPath) => `${kbHref(categoryPath)}new/`;
export const kbSearchHref = (q) => (q ? `/search/?q=${encodeURIComponent(q)}` : '/search/');

// Language editions (docs/kb-specs/P7-SPEC.md §4.1). `code` is the edition's URL code —
// `urlLang(kb, locale)` from ./lang.js: '' for the DEFAULT language (served at the root,
// exactly the helpers above), else the lowercase locale ('es', 'pt-br'). Stored paths stay
// language-relative; only these helpers add the `/{code}` prefix.
//   langPrefixed('', 'a/b') → 'a/b' · ('es', 'a/b') → 'es/a/b' · ('es', '') → 'es'
export const langPrefixed = (code, path) => (code ? (path ? `${code}/${path}` : code) : path);
// ('', '') → '/' · ('es', '') → '/es/' · ('es', 'a/b') → '/es/a/b/'
export const kbHrefLang = (code, path) => kbHref(langPrefixed(code, path));
export const kbEditHrefLang = (code, path) => `${kbHrefLang(code, path)}edit/`;
export const kbNewHrefLang = (code, categoryPath) => `${kbHrefLang(code, categoryPath)}new/`;
export const kbSearchHrefLang = (code, q) => {
  const base = kbHrefLang(code, 'search');
  return q ? `${base}?q=${encodeURIComponent(q)}` : base;
};
export const kbTagsHrefLang = (code) => kbHrefLang(code, 'tags');
export const kbFeedHrefLang = (code) => (code ? `/${code}/feed.xml` : '/feed.xml');

// Split a request pathname into slug segments (no empties).
export function kbSegments(pathname) {
  return String(pathname || '/').split('/').filter(Boolean);
}

// The canonical spelling of a KB request path: lowercase, single slashes, trailing slash.
// `redirect` is true when the incoming pathname differs (→ 301 to `canonical`). The query
// string is the caller's concern. Percent-encoded input is left encoded — no valid slug
// contains an escape, so it simply won't resolve.
export function canonicalKbPath(pathname) {
  const raw = String(pathname || '/');
  const segments = kbSegments(raw.toLowerCase());
  const canonical = segments.length ? `/${segments.join('/')}/` : '/';
  return { canonical, segments, redirect: canonical !== raw };
}

// Depth of a stored category path ('a' → 1, 'a/b/c' → 3).
export const kbPathDepth = (path) => (path ? String(path).split('/').length : 0);

export const withinKbDepth = (depth) => depth >= 1 && depth <= KB_LIMITS.MAX_DEPTH;
